import { createI18n } from "vue-i18n";
import { flowWordsMessages } from "@/rules/i18n/flow-words";
import { commonMessages } from "./common";
import { dynamicFormMessages } from "./dynamic-form";
import { errorMessages } from "./errors";
import { type AppLocale, initialLocale, writeStoredLocale } from "./locales";

/**
 * THE SEED of internationalization — one instance, installed once.
 *
 * The approved billing plan (section 10.4) says billing screens are born with
 * their own vue-i18n dictionary: ENGLISH as the source language, pt-BR as a
 * translation. Each feature that speaks i18n brings its own dictionary and
 * mounts it with `useI18n({ messages })` in local scope, so the project of
 * translating the whole app only has to plug dictionaries in — never to rewire
 * the setup.
 *
 * `fallback` is `en` because English is the origin — a key missing from a
 * translation falls back to the source sentence, never to the raw key.
 *
 * ── WHY THE GLOBAL SCOPE CARRIES A DICTIONARY NOW ─────────────────────────
 *
 * It used to carry none, and that was right while only billing spoke i18n. What
 * a feature cannot own is the word that is the SAME on every screen — "Save",
 * "Loading…", and the map from a server error CODE to a sentence. Copied into
 * each feature, those become forty translations of "Cancel" that drift.
 *
 * So exactly what CROSSES lives in the root composer, and nothing else. vue-i18n
 * falls a local scope back to the root when a key is missing (`fallbackRoot`),
 * which means a feature component with its own dictionary writes `t('action.save')`
 * and gets it — without importing anything or mounting a second scope.
 *
 * ── WHAT CHANGED WHEN THE PANEL LEARNED TO SPEAK TWO LANGUAGES ────────────
 *
 * `locale` used to be the literal `"pt-BR"`, with the comment "because that is
 * what the product speaks today". It is now RESOLVED: the stored hint, then the
 * browser, then English (`initialLocale`), and refined to the full cascade —
 * person, then workspace — the moment the session answers, by `setPanelLocale`.
 * The wiring is untouched; only the value it starts at is now a decision instead
 * of a constant.
 */
/**
 * A semente, como FUNÇÃO — para que exista uma só.
 *
 * ── O QUE ISTO CONSERTA ───────────────────────────────────────────────────
 *
 * Doze arquivos chamavam `createI18n` a dizer "a mesma semente que o app
 * instala", e só quatro traziam `missingWarn`/`fallbackWarn`. As outras oito
 * ligavam de volta os avisos que a linha abaixo existe para calar — e três
 * specs que afirmam "monta sem um único aviso" liam
 * `[intlify] Not found 'action.close' key in 'pt' locale messages` e falhavam.
 * O aviso descrevia o DESENHO a funcionar: um escopo local que não tem a
 * palavra cai na raiz, que a tem. O caminho certo, contado como avaria.
 *
 * Uma cópia de uma semente não é a semente. Agora é uma função, e quem quer a
 * do app pede-a — o `locale` é o único parâmetro, porque um teste precisa de
 * fixar a língua e o app precisa de a resolver.
 */
export function panelI18n(locale: AppLocale = initialLocale()) {
	return createI18n({
		legacy: false,
		locale,
		fallbackLocale: "en",
		// Silence "fall back to..." console noise: falling back to EN is the design.
		missingWarn: false,
		fallbackWarn: false,
		messages: {
			// `dynamicForm` entra na RAIZ com as outras duas porque quem pede estas
			// frases não é um componente: são os validadores e os resolvedores puros
			// de `src/dynamic/`, que chamam `i18n.global.t` e só alcançam a raiz.
			// `flowWords` pelo mesmo motivo: os nomes dos passos de um fluxo saem de
			// funções puras de `src/rules/` (`flowSteps`, a frase da regra).
			en: {
				...commonMessages.en,
				...errorMessages.en,
				...dynamicFormMessages.en,
				...flowWordsMessages.en,
			},
			"pt-BR": {
				...commonMessages["pt-BR"],
				...errorMessages["pt-BR"],
				...dynamicFormMessages["pt-BR"],
				...flowWordsMessages["pt-BR"],
			},
			// O grego monta na RAIZ pelo mesmo motivo que os outros dois: um idioma
			// que existe em `SUPPORTED_LOCALES` mas não aqui deixa a moldura falar
			// grego enquanto os validadores e resolvedores de `src/dynamic/` — que
			// chamam `i18n.global.t` e só alcançam a raiz — respondem em inglês.
			// Meia tela traduzida é o defeito, e é silencioso: o fallback funciona.
			el: {
				...commonMessages.el,
				...errorMessages.el,
				...dynamicFormMessages.el,
				...flowWordsMessages.el,
			},
			// Espanhol, francês, italiano e alemão entram pela mesma porta e pelo
			// mesmo motivo do grego. A raiz é o único escopo que os validadores e os
			// resolvedores puros de `src/dynamic/` alcançam: um idioma listado em
			// `SUPPORTED_LOCALES` e ausente daqui dá moldura traduzida com
			// formulários em inglês — e o fallback esconde o defeito.
			es: {
				...commonMessages.es,
				...errorMessages.es,
				...dynamicFormMessages.es,
				...flowWordsMessages.es,
			},
			fr: {
				...commonMessages.fr,
				...errorMessages.fr,
				...dynamicFormMessages.fr,
				...flowWordsMessages.fr,
			},
			it: {
				...commonMessages.it,
				...errorMessages.it,
				...dynamicFormMessages.it,
				...flowWordsMessages.it,
			},
			de: {
				...commonMessages.de,
				...errorMessages.de,
				...dynamicFormMessages.de,
				...flowWordsMessages.de,
			},
		},
	});
}

/** THE instance, installed once by `main.ts`. */
export const i18n = panelI18n();

/**
 * Switch the panel's language, now — no reload, and it survives one.
 *
 * The three things that have to happen together, which is why they are one
 * function and not three lines in a component:
 *
 *   1. `i18n.global.locale` — every `useI18n({ inheritLocale: true })` in the app
 *      is reactive to it, so every screen re-renders in the new language on the
 *      same tick. This is what makes the change IMMEDIATE.
 *   2. `<html lang>` — a screen reader announces in the wrong accent without it,
 *      and the browser's own translate prompt reads it. It is the one piece of
 *      the page vue-i18n does not own.
 *   3. The stored hint — so the next boot paints in the right language before
 *      the profile has answered.
 *
 * It does NOT write to the server. Persisting the PERSON's choice is a profile
 * write and belongs to the screen that offers the choice; this same function is
 * also called when the cascade merely RESOLVES a language nobody chose, and
 * saving that would turn a fallback into a preference.
 */
export function setPanelLocale(locale: AppLocale): void {
	i18n.global.locale.value = locale;
	if (typeof document !== "undefined") document.documentElement.setAttribute("lang", locale);
	writeStoredLocale(locale);
}

/** The language the panel is being read in right now. */
export function currentPanelLocale(): AppLocale {
	return i18n.global.locale.value as AppLocale;
}

export {
	type AppLocale,
	DEFAULT_LOCALE,
	LOCALE_NAMES,
	normalizeLocale,
	type PanelLocaleSources,
	resolvePanelLocale,
	SUPPORTED_LOCALES,
} from "./locales";
